// packages/web/src/components/marketplace/BuyBox.tsx
// Amazon-style Buy Box — winner-first pattern with single CTA and trust bullets
import Check from '@mui/icons-material/Check';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import { Box, Button, Collapse, Typography } from '@mui/material';
import React, { useState } from 'react';

import AddToCartButton from './AddToCartButton';
import BuyBoxOption from './BuyBoxOption';
import { useBuyBoxLogic } from '../../hooks/useBuyBoxLogic';
import { PerkQuoteContext } from '../../utils/perkRules';

interface ServiceData {
  // Required MarketplaceService fields
  id: string;
  name: string;
  description: string;
  category: string;
  brand_name: string;
  pricing_model: 'fixed' | 'varying' | 'variants' | 'multi_frequency';
  is_flagship: boolean;
  status: 'active' | 'inactive';
  requires_address_verification: boolean;

  // Optional MarketplaceService fields
  brand_description?: string;
  brand_logo_path?: string;
  default_pricing?: any;
  variants?: any;
  frequencies?: any[];
  geographic_restrictions?: any;
  metadata?: any;

  // Legacy/compatibility fields
  price_type?: string;
  product_type?: string;
  commission_rate?: number;
  service_type?: string;
  average_rating?: number;
  review_count?: number;
  owner_tenant_id?: string;
  owner_company?: string;
  company_description?: string;
  perks?: any[];
  winningPerk?: any;
  allPerks?: any[];
  one_time_charge?: number;
  nrc?: number;

  // Pricing data from API response (relational JOINs) - now nested under pricing object
  pricing?: {
    core_rate?: number;
    core_rate_min?: number;
    core_rate_max?: number;
    pricing_model?: string;
    billing_frequency?: string;
    varying_price_enabled?: boolean;
  };

  // Legacy fields for backward compatibility
  core_rate?: number;
  nrc_rate?: number;
  shipping_rate?: number;
  other_rate?: number;
  varying_price_enabled?: boolean;
}

interface BuyBoxProps {
  service: ServiceData;
  selectedVariants?: Record<string, string>;
  selectedVariantPrice?: number | null;
  selectedVariantKey?: string;
  selectedTerm?: number | null;
  serviceConfiguration?: Record<string, any>;
  ctaLabel?: string;
  ctaDisabled?: boolean;
}

const TRUST_BULLETS_BASE = [
  'Managed installation included',
  '99.99% uptime SLA',
  '24/7 NOC support',
];
const PERK_BULLET = 'Perk delivery guaranteed by Aithon';

export function getServiceTrustBullets(service: Pick<ServiceData, 'metadata'>): string[] {
  const configured = Array.isArray(service.metadata?.trust_bullets)
    ? service.metadata.trust_bullets
        .map((bullet: unknown) => String(bullet || '').trim())
        .filter(Boolean)
    : [];

  return configured.length ? configured : TRUST_BULLETS_BASE;
}

export const BuyBox: React.FC<BuyBoxProps> = ({
  service,
  selectedVariants = {},
  selectedVariantPrice,
  selectedVariantKey,
  selectedTerm,
  serviceConfiguration,
  ctaLabel,
  ctaDisabled = false,
}) => {
  const [showOtherOptions, setShowOtherOptions] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);

  const quoteContext = React.useMemo<PerkQuoteContext>(() => ({
    selectedVariantKey,
    selectedTermMonths: selectedTerm,
    monthlyRecurringCents: selectedVariantPrice,
    serviceConfiguration,
  }), [selectedVariantKey, selectedTerm, selectedVariantPrice, serviceConfiguration]);

  const { sortedOptions, getPartnerDisplayName } = useBuyBoxLogic({ service, quoteContext });

  React.useEffect(() => {
    if (selectedOptionIndex >= sortedOptions.length) setSelectedOptionIndex(0);
  }, [selectedOptionIndex, sortedOptions.length]);

  if (sortedOptions.length === 0) return null;

  const winnerOption = sortedOptions[selectedOptionIndex];

  const handleSelectOther = (absoluteIndex: number) => {
    setSelectedOptionIndex(absoluteIndex);
    setShowOtherOptions(false);
  };

  const selectedPartnerName = getPartnerDisplayName(winnerOption);

  // Other options = all options except the currently selected winner
  const otherOptions = sortedOptions
    .map((opt, i) => ({ opt, i }))
    .filter(({ i }) => i !== selectedOptionIndex);

  // Does the winner have actual perks?
  const hasPerks = winnerOption?.perks?.length > 0 || winnerOption?.winningPerk || winnerOption?.allPerks?.length > 0 || winnerOption?.perk_type;
  // No-perk state: only a synthetic base record, no real competing partners
  const isNoPerkState = !hasPerks && (sortedOptions.length <= 1 || sortedOptions[0]?.id === 'base');
  const serviceTrustBullets = getServiceTrustBullets(service);
  const trustBullets = hasPerks ? [...serviceTrustBullets, PERK_BULLET] : serviceTrustBullets;
  const usesDirectProviderBilling = Boolean(service.metadata?.progressive_disclosure);
  const providerName = service.metadata?.supplier || service.brand_name || 'the provider';
  const checkoutSubtext = usesDirectProviderBilling
    ? `Billed directly by ${providerName} after your order is confirmed`
    : 'No commitment until you confirm · Free to start';

  // ── No-perk state: skip the competitive UI entirely, just show the CTA ──
  if (isNoPerkState) {
    return (
      <Box sx={{ mb: 3, bgcolor: 'white' }}>
        {/* CTA */}
        <Box
          sx={{
            mb: 0.5,
            '& .MuiButton-containedPrimary, & .MuiButton-contained': {
              backgroundColor: '#3b82f6',
              '&:hover': { backgroundColor: '#2563eb' },
            },
          }}
        >
          <AddToCartButton
            serviceId={service.id}
            label={ctaLabel || 'Get This Service'}
            selectedOption={winnerOption}
            variant="contained"
            size="large"
            fullWidth
            selectedVariants={selectedVariants}
            selectedVariantPrice={selectedVariantPrice}
            selectedVariantKey={selectedVariantKey}
            basePrice={(service as any).pricing?.core_rate || 0}
            monthlyRate={(service as any).pricing?.core_rate || 0}
            serviceType={service.product_type || service.service_type}
            varyingPriceEnabled={service.varying_price_enabled}
            serviceConfiguration={serviceConfiguration}
            disabled={ctaDisabled}
          />
        </Box>

        {/* Subtext */}
        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mt: 1, mb: 2 }}
        >
          {checkoutSubtext}
        </Typography>

        {/* Trust bullets */}
        <Box>
          {serviceTrustBullets.map((bullet) => (
            <Box key={bullet} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Check sx={{ fontSize: '0.9rem', color: 'success.main', flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">{bullet}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3, bgcolor: 'white' }}>
      {/* Section label — only shown when perks are competing */}
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          fontWeight: 700,
          letterSpacing: 1,
          mb: 1.5,
          display: 'block',
          fontSize: '0.7rem',
        }}
      >
        INCLUDED WITH YOUR ORDER
      </Typography>

      {/* Winner card */}
      <Box
        key={winnerOption?.id || selectedOptionIndex}
        aria-live="polite"
        sx={{
          '@keyframes selectedPerkSwapIn': {
            '0%': { opacity: 0, transform: 'translateY(12px) scale(0.985)' },
            '55%': { opacity: 1, transform: 'translateY(-3px) scale(1.01)' },
            '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
          '@keyframes selectedPerkGlow': {
            '0%, 100%': { boxShadow: '0 0 0 rgba(59, 130, 246, 0)' },
            '35%': { boxShadow: '0 18px 42px rgba(59, 130, 246, 0.22)' },
          },
          animation: 'selectedPerkSwapIn 420ms cubic-bezier(0.2, 0.8, 0.2, 1), selectedPerkGlow 900ms ease-out',
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
          },
        }}
      >
        <BuyBoxOption
          option={winnerOption}
          isWinner={true}
          isFirst={selectedOptionIndex === 0}
          getPartnerDisplayName={getPartnerDisplayName}
        />
      </Box>

      {/* "Get This Service" CTA */}
      <Box
        sx={{
          mb: 0.5,
          '& .MuiButton-containedPrimary, & .MuiButton-contained': {
            backgroundColor: '#3b82f6',
            '&:hover': { backgroundColor: '#2563eb' },
          },
        }}
      >
        <AddToCartButton
          serviceId={service.id}
          label={ctaLabel || 'Get This Service'}
          selectedOption={winnerOption}
          variant="contained"
          size="large"
          fullWidth
          selectedVariants={selectedVariants}
          selectedVariantPrice={selectedVariantPrice}
          selectedVariantKey={selectedVariantKey}
          basePrice={(service as any).pricing?.core_rate || 0}
          monthlyRate={(service as any).pricing?.core_rate || 0}
          serviceType={service.product_type || service.service_type}
          varyingPriceEnabled={service.varying_price_enabled}
          serviceConfiguration={serviceConfiguration}
          disabled={ctaDisabled}
        />
      </Box>

      {/* Subtext */}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          textAlign: 'center',
          color: 'text.secondary',
          mt: 1,
          mb: 2,
        }}
      >
        {checkoutSubtext}
      </Typography>

      {/* Trust bullets */}
      <Box>
        {trustBullets.map((bullet) => (
          <Box
            key={bullet}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
          >
            <Check sx={{ fontSize: '0.9rem', color: 'success.main', flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary">
              {bullet}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Other options toggle and cards (below CTA + trust content) */}
      {sortedOptions.length > 1 && (
        <Box sx={{ mt: 2.5 }}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={() => setShowOtherOptions((v) => !v)}
            aria-expanded={showOtherOptions}
            aria-controls="buy-box-other-options"
            startIcon={<SwapHoriz />}
            endIcon={showOtherOptions ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            sx={{
              justifyContent: 'space-between',
              px: 1.5,
              py: 1.2,
              borderRadius: 2,
              borderWidth: 1.5,
              borderColor: '#93c5fd',
              bgcolor: '#eff6ff',
              color: '#1d4ed8',
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 800,
              boxShadow: '0 8px 22px rgba(37, 99, 235, 0.12)',
              '&:hover': {
                borderWidth: 1.5,
                borderColor: '#2563eb',
                bgcolor: '#dbeafe',
                boxShadow: '0 12px 30px rgba(37, 99, 235, 0.18)',
                transform: 'translateY(-1px)',
              },
              transition: 'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&:hover': { transform: 'none' },
              },
            }}
          >
            {showOtherOptions
              ? 'Hide other perk options'
              : `Compare ${sortedOptions.length - 1} more perk option${sortedOptions.length === 2 ? '' : 's'}`}
          </Button>

          <Collapse in={showOtherOptions} timeout={320} unmountOnExit>
            <Box
              id="buy-box-other-options"
              sx={{
                mt: 1.25,
                p: 1,
                border: '1px solid #dbeafe',
                borderRadius: 2,
                bgcolor: '#f8fbff',
              }}
            >
              <Typography
                variant="caption"
                sx={{ display: 'block', color: '#475569', fontWeight: 700, mb: 1 }}
              >
                Choose a different partner/perk. Your selected offer will animate into the top card.
              </Typography>
              {otherOptions.map(({ opt, i }) => (
                <BuyBoxOption
                  key={opt.id || i}
                  option={opt}
                  isWinner={false}
                  isFirst={i === 0}
                  getPartnerDisplayName={getPartnerDisplayName}
                  onSelect={() => handleSelectOther(i)}
                />
              ))}
            </Box>
          </Collapse>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1,
              color: '#64748b',
              textAlign: 'center',
            }}
          >
            Currently selected: {selectedPartnerName}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default BuyBox;
